// src/hooks/useVirtualSensorApi.tsx

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

type VirtualSensorContextValue = {
  data: any[]
  setData: Dispatch<SetStateAction<any[]>>
  refresh: (_?: { force?: boolean }) => Promise<any>
}

const UNKNOWN_ERROR = 'Unbekannter Fehler'

const VirtualSensorCtx = createContext<VirtualSensorContextValue>({
  data: [],
  setData: () => {},
  refresh: async () => [],
})

type Props = {
  children: ReactNode
}

export const VirtualSensorDataProvider = ({ children }: Props) => {
  const [virtualSensors, setVirtualSensors] = useState([])
  const vsReqRef = useRef(null)

  const withRequest = useCallback(async fn => {
    try {
      await refreshToken()
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (error) {
          const statusCode = error?.status ?? error?.response?.status
          notifyError(`vSensorApi: GET sensors ${statusCode}`)
          return null
        }
      }
      const msg =
        error?.message || (typeof error === 'string' ? error : UNKNOWN_ERROR)
      notifyError(`vSensorApi: ${msg}${status ? ` (${status})` : ''}`)
      return null
    }
  }, [])

  const fetchVirtualSensors = useCallback(
    async ({ force = false } = {}) => {
      if (vsReqRef.current && !force) return vsReqRef.current

      vsReqRef.current = (async () => {
        try {
          const data = await withRequest(async () => {
            const url = `${REACT_APP_API_URL}/virtualsensor/combined`
            const options = { method: 'GET', headers: { ...authHeader() } }
            const response = await fetch(url, options)

            if (response.status === 204) return []

            if (!response.ok) {
              const text = await response.text().catch(() => '')
              throw {
                message: text || `HTTP ${response.status}`,
                status: response.status,
              }
            }

            const payload = await response.json()
            return payload?.items ?? payload?.data ?? []
          })

          if (Array.isArray(data)) setVirtualSensors(data)
          return Array.isArray(data) ? data : []
        } finally {
          vsReqRef.current = null
        }
      })()

      return vsReqRef.current
    },
    [withRequest]
  )

  useEffect(() => {
    if (virtualSensors.length > 0) return
    ;(async () => {
      try {
        await fetchVirtualSensors()
      } catch (error) {
        const status = error?.status ?? error?.response?.status
        const msg =
          error?.message || (typeof error === 'string' ? error : UNKNOWN_ERROR)
        notifyError(`vSensorApi: ${msg}${status ? ` (${status})` : ''}`)
      }
    })()
  }, [virtualSensors.length, fetchVirtualSensors])

  const ctxValue = useMemo(
    () => ({
      data: virtualSensors,
      setData: setVirtualSensors,
      refresh: fetchVirtualSensors,
    }),
    [virtualSensors, fetchVirtualSensors]
  )

  return (
    <VirtualSensorCtx.Provider value={ctxValue}>
      {children}
    </VirtualSensorCtx.Provider>
  )
}

export const useVirtualSensorPool = () => useContext(VirtualSensorCtx).data

export const useVirtualSensorApi = () => {
  const { refresh: refreshVirtualSensors } = useContext(VirtualSensorCtx)

  const withRequest = useCallback(async fn => {
    try {
      await refreshToken()
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          const s = e2?.status ?? e2?.response?.status
          const msg =
            e2?.message || (typeof e2 === 'string' ? e2 : UNKNOWN_ERROR)
          notifyError(`vSensorApi: ${msg}${s ? ` (${s})` : ''}`)
          return null
        }
      }
      const msg =
        error?.message || (typeof error === 'string' ? error : UNKNOWN_ERROR)
      notifyError(`vSensorApi: ${msg}${status ? ` (${status})` : ''}`)
      return null
    }
  }, [])

  const recordDataSave = useCallback(data => {
    const { name, originalSensorId } = data
    return {
      data: {
        attributes: {
          virtual_sensor_name: name,
          sensors_to_be_combined: originalSensorId,
        },
      },
    }
  }, [])

  const recordDataUpdate = useCallback(data => {
    const {
      combine_id,
      combined_virtual_sensor_id,
      combined_virtual_sensor_device_id,
      name,
      originalSensorId,
      sensor_type,
    } = data
    return {
      data: [
        {
          attributes: {
            combine_id,
            combined_virtual_sensor_id,
            combined_virtual_sensor_device_id,
            original_sensors_id: originalSensorId,
            virtual_sensor_type: sensor_type,
            name,
          },
          id: combined_virtual_sensor_id?.toString(),
          links: { self: '/virtualsensor/combined' },
          type: 'virtual_sensor',
        },
      ],
    }
  }, [])

  const save = useCallback(
    async data =>
      withRequest(async () => {
        const { originalSensorId } = data || {}
        if (!originalSensorId) {
          notifyError(LC.VIRTUAL_SENSOR_ID_REQUIRED)
          return null
        }

        const url = `${REACT_APP_API_URL}/virtualsensor/combined`
        const options = {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(recordDataSave(data)),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }
        const result = await response.json()
        notifySuccess(LC.VIRTUAL_SENSOR_SAVED)

        await refreshVirtualSensors({ force: true })
        return result?.id ?? null
      }),
    [recordDataSave, refreshVirtualSensors, withRequest]
  )

  const update = useCallback(
    async data =>
      withRequest(async () => {
        const { originalSensorId, combined_virtual_sensor_id } = data || {}
        if (!originalSensorId) {
          notifyError(LC.VIRTUAL_SENSOR_ID_REQUIRED)
          return null
        }

        const url = `${REACT_APP_API_URL}/virtualsensor/combined/${combined_virtual_sensor_id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'PATCH',
          body: JSON.stringify(recordDataUpdate(data)),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }
        notifySuccess(LC.VIRTUAL_SENSOR_UPDATED)
        await refreshVirtualSensors({ force: true })
        return combined_virtual_sensor_id ?? null
      }),
    [recordDataUpdate, refreshVirtualSensors, withRequest]
  )

  const remove = useCallback(
    async id =>
      withRequest(async () => {
        if (!id) {
          notifyError(LC.VIRTUAL_SENSOR_ID_REQUIRED)
          return false
        }

        const url = `${REACT_APP_API_URL}/virtualsensor/combined/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'DELETE',
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        notifySuccess(LC.VIRTUAL_SENSOR_DELETED)

        await refreshVirtualSensors({ force: true })
        return true
      }),
    [refreshVirtualSensors, withRequest]
  )

  return { save, update, remove, refreshVirtualSensors }
}
